import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError, Package } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const PackagesStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  const [newPackage, setNewPackage] = useState<Partial<Package>>({
    name: '',
    description: '',
    price: 0,
    includes: []
  });
  
  const [weeklyPrice, setWeeklyPrice] = useState<number>(0);
  const [monthlyPrice, setMonthlyPrice] = useState<number>(0);
  const [fullLeaguePrice, setFullLeaguePrice] = useState<number>(0);

  const handleAddPackage = () => {
    if (!newPackage.name || !newPackage.description || !newPackage.price || !newPackage.includes || newPackage.includes.length === 0) {
      addError('packages', 'Please fill in all package fields and select at least one squad');
      return;
    }

    const packages = data.packages || [];
    const packageToAdd: Package = {
      name: newPackage.name!,
      description: newPackage.description!,
      price: newPackage.price!,
      includes: newPackage.includes!
    };

    updateData({
      packages: [...packages, packageToAdd]
    });

    // Reset form
    setNewPackage({
      name: '',
      description: '',
      price: 0,
      includes: []
    });
    clearErrors();
  };

  const handleSquadToggle = (squadId: number) => {
    const currentIncludes = newPackage.includes || [];
    const isSelected = currentIncludes.includes(squadId);
    
    if (isSelected) {
      setNewPackage({ 
        ...newPackage, 
        includes: currentIncludes.filter(id => id !== squadId) 
      });
    } else {
      setNewPackage({ 
        ...newPackage, 
        includes: [...currentIncludes, squadId] 
      });
    }
  };

  const getSquadName = (squadId: number) => {
    const squad = data.squads?.find(s => s.id === squadId);
    return squad ? squad.name : `Squad ${squadId}`;
  };

  const handleRemovePackage = (index: number) => {
    const packages = data.packages || [];
    const updatedPackages = packages.filter((_, i) => i !== index);
    updateData({ packages: updatedPackages });
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  const handleGenerateWeeklyPackages = () => {
    if (!data.squads || data.squads.length === 0) {
      addError('packages', 'No squads available to generate packages');
      return;
    }

    if (!weeklyPrice || weeklyPrice <= 0) {
      addError('packages', 'Please enter a valid weekly package price');
      return;
    }

    // Group squads by week
    const squadsByWeek: { [key: string]: typeof data.squads } = {};
    
    data.squads.forEach((squad) => {
      const squadDate = new Date(squad.date);
      // Get the week number (simple week calculation)
      const weekKey = `Week ${Math.floor((squadDate.getTime() - new Date(data.startDate!).getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1}`;
      
      if (!squadsByWeek[weekKey]) {
        squadsByWeek[weekKey] = [];
      }
      squadsByWeek[weekKey].push(squad);
    });

    // Create packages for each week
    const generatedPackages: Package[] = [];
    Object.entries(squadsByWeek).forEach(([weekName, squads]) => {
      // Use squad index as ID since IDs aren't assigned until backend saves them
      const squadIds = squads.map(s => {
        const squadIndex = data.squads!.findIndex(sq => 
          sq.name === s.name && sq.date === s.date && sq.start_time === s.start_time
        );
        return squadIndex >= 0 ? squadIndex : (s.id || 0);
      });
      const squadNames = squads.map(s => s.name).join(', ');
      
      generatedPackages.push({
        name: weekName,
        description: t('admin.createLeague.steps.packagesStep.autoGenerate.packageDescription', {
          squadNames: squadNames
        }),
        price: weeklyPrice,
        includes: squadIds
      });
    });

    // Add generated packages to existing ones
    const existingPackages = data.packages || [];
    updateData({
      packages: [...existingPackages, ...generatedPackages]
    });

    clearErrors();
  };

  const handleGenerateMonthlyPackages = () => {
    if (!data.squads || data.squads.length === 0) {
      addError('packages', 'No squads available to generate packages');
      return;
    }

    if (!monthlyPrice || monthlyPrice <= 0) {
      addError('packages', 'Please enter a valid monthly package price');
      return;
    }

    // Group squads by month
    const squadsByMonth: { [key: string]: typeof data.squads } = {};
    
    data.squads.forEach((squad) => {
      const squadDate = new Date(squad.date);
      const monthKey = `Month ${squadDate.getMonth() + 1} (${squadDate.toLocaleString('default', { month: 'long' })})`;
      
      if (!squadsByMonth[monthKey]) {
        squadsByMonth[monthKey] = [];
      }
      squadsByMonth[monthKey].push(squad);
    });

    // Only create packages for months that have squads
    const generatedPackages: Package[] = [];
    const monthEntries = Object.entries(squadsByMonth);
    
    // If we only have enough squads for less than 2 months, create what we can
    monthEntries.forEach(([monthName, squads]) => {
      // Use squad index as ID since IDs aren't assigned until backend saves them
      const squadIds = squads.map(s => {
        const squadIndex = data.squads!.findIndex(sq => 
          sq.name === s.name && sq.date === s.date && sq.start_time === s.start_time
        );
        return squadIndex >= 0 ? squadIndex : (s.id || 0);
      });
      const squadNames = squads.map(s => s.name).join(', ');
      
      generatedPackages.push({
        name: monthName,
        description: t('admin.createLeague.steps.packagesStep.autoGenerate.packageDescription', {
          squadNames: squadNames
        }),
        price: monthlyPrice,
        includes: squadIds
      });
    });

    // Add generated packages to existing ones
    const existingPackages = data.packages || [];
    updateData({
      packages: [...existingPackages, ...generatedPackages]
    });

    clearErrors();
  };

  const handleGenerateFullLeaguePackage = () => {
    if (!data.squads || data.squads.length === 0) {
      addError('packages', 'No squads available to generate package');
      return;
    }

    if (!fullLeaguePrice || fullLeaguePrice <= 0) {
      addError('packages', 'Please enter a valid full league package price');
      return;
    }

    // Get all squad IDs (use indices since IDs aren't assigned until backend saves)
    const allSquadIds = data.squads.map((squad, index) => {
      return squad.id !== undefined ? squad.id : index;
    });
    const allSquadNames = data.squads.map(s => s.name).join(', ');

    // Create the full league package
    const fullLeaguePackage: Package = {
      name: 'Full League Package',
      description: t('admin.createLeague.steps.packagesStep.autoGenerate.fullLeaguePackageDescription', {
        count: data.squads.length,
        squadNames: allSquadNames
      }),
      price: fullLeaguePrice,
      includes: allSquadIds
    };

    // Add to existing packages
    const existingPackages = data.packages || [];
    updateData({
      packages: [...existingPackages, fullLeaguePackage]
    });

    clearErrors();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.packagesStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.packagesStep.subtitle')}
        </p>
      </div>

      {/* Existing Packages */}
      {data.packages && data.packages.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('admin.createLeague.steps.packagesStep.currentPackages')}</h3>
          {data.packages.map((pkg, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold text-white">{pkg.name}</h4>
                  <p className="text-purple-200 text-sm mt-1">{pkg.description}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-purple-300">
                    <span>{t('admin.createLeague.steps.packagesStep.price')} ${pkg.price}</span>
                  </div>
                  <div className="mt-2 text-sm text-purple-200">
                    <span className="font-medium">{t('admin.createLeague.steps.packagesStep.includes')}</span> {
                      Array.isArray(pkg.includes) && pkg.includes.length > 0
                        ? pkg.includes.map(squadId => getSquadName(squadId)).join(', ')
                        : t('admin.createLeague.steps.packagesStep.noSquadsSelected')
                    }
                  </div>
                </div>
                <button
                  onClick={() => handleRemovePackage(index)}
                  className="text-red-400 hover:text-red-300 ml-4"
                >
                  {t('admin.createLeague.steps.packagesStep.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Package Form */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.packagesStep.addNewPackage')}</h3>
        
        {/* Auto-Generate Packages Section */}
        {data.squads && data.squads.length > 0 && (
          <div className="mb-6 bg-purple-600/10 rounded-lg p-4 border border-purple-400/30">
            <h4 className="text-md font-semibold text-purple-200 mb-3">{t('admin.createLeague.steps.packagesStep.autoGenerate.title')}</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Weekly Packages Generator */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.generateWeekly')}
                </label>
                <div className="flex flex-col gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={weeklyPrice}
                    onChange={(e) => setWeeklyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t('admin.createLeague.steps.packagesStep.autoGenerate.pricePerWeek')}
                  />
                  <button
                    onClick={handleGenerateWeeklyPackages}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
                  >
                    {t('admin.createLeague.steps.packagesStep.autoGenerate.generateWeeklyButton')}
                  </button>
                </div>
                <p className="text-xs text-purple-300 mt-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.weeklyNote')}
                </p>
              </div>

              {/* Monthly Packages Generator */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.generateMonthly')}
                </label>
                <div className="flex flex-col gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyPrice}
                    onChange={(e) => setMonthlyPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t('admin.createLeague.steps.packagesStep.autoGenerate.pricePerMonth')}
                  />
                  <button
                    onClick={handleGenerateMonthlyPackages}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                  >
                    {t('admin.createLeague.steps.packagesStep.autoGenerate.generateMonthlyButton')}
                  </button>
                </div>
                <p className="text-xs text-purple-300 mt-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.monthlyNote')}
                </p>
              </div>

              {/* Full League Package Generator */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.generateFullLeague')}
                </label>
                <div className="flex flex-col gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={fullLeaguePrice}
                    onChange={(e) => setFullLeaguePrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t('admin.createLeague.steps.packagesStep.autoGenerate.fullLeaguePrice')}
                  />
                  <button
                    onClick={handleGenerateFullLeaguePackage}
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
                  >
                    {t('admin.createLeague.steps.packagesStep.autoGenerate.generateFullLeagueButton')}
                  </button>
                </div>
                <p className="text-xs text-purple-300 mt-2">
                  {t('admin.createLeague.steps.packagesStep.autoGenerate.fullLeagueNote', { count: data.squads.length })}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.packagesStep.packageName')} *
            </label>
            <input
              type="text"
              value={newPackage.name}
              onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.packagesStep.packageNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.packagesStep.price')} *
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={newPackage.price}
              onChange={(e) => setNewPackage({ ...newPackage, price: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.packagesStep.pricePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.packagesStep.selectSquads')} *
            </label>
            {data.squads && data.squads.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                {data.squads.map((squad, index) => {
                  const squadId = squad.id || index; // Use squad.id if available, otherwise use index
                  const isSelected = (newPackage.includes || []).includes(squadId);
                  
                  return (
                    <label
                      key={squadId}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition duration-200 ${
                        isSelected
                          ? 'bg-purple-600/20 border-purple-400 text-purple-200'
                          : 'bg-white/5 border-white/20 text-purple-300 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSquadToggle(squadId)}
                        className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{squad.name}</div>
                        <div className="text-xs opacity-75">
                          {squad.date} • {squad.start_time} - {squad.end_time} • {squad.games_per_squad} {squad.games_per_squad === 1 ? t('admin.createLeague.steps.squadsStep.gameSingular') : t('admin.createLeague.steps.squadsStep.gamePlural')}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-4">
                <p className="text-yellow-200 text-sm">
                  {t('admin.createLeague.steps.packagesStep.noSquadsAvailable')}
                </p>
              </div>
            )}
            <p className="text-xs text-purple-300 mt-2">
              {t('admin.createLeague.steps.packagesStep.squadsHelperText')}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.packagesStep.description')} *
          </label>
          <textarea
            rows={3}
            value={newPackage.description}
            onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder={t('admin.createLeague.steps.packagesStep.descriptionPlaceholder')}
          />
        </div>

        <button
          onClick={handleAddPackage}
          className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
        >
          {t('admin.createLeague.steps.packagesStep.addPackage')}
        </button>

        {getFieldError('packages') && (
          <p className="text-red-400 text-sm mt-2">{getFieldError('packages')}</p>
        )}
      </div>

      {(!data.packages || data.packages.length === 0) && (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 text-center">
          <p className="text-yellow-200">
            {t('admin.createLeague.steps.packagesStep.noPackagesWarning')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PackagesStep;